/**
 * lib/realtime.js (Compatibility Wrapper)
 *
 * IMPORTANT:
 * - DO NOT create a second Ably client here.
 * - This wraps services/realtime.js so the app has ONE realtime system.
 *
 * Existing callers (Chat.js etc.) can keep importing:
 * - subscribeToEvent
 * - subscribeToMatchMessages
 * - getRealtimeClient
 */

import { getRealtimeClient as getSvcClient, getSafeChannel } from '../services/realtime';

/**
 * Safe detach helper (never throws)
 */
export function safeDetachAblyChannel(channel, label = 'realtime') {
  if (!channel) return;

  try {
    const state = channel.state;
    console.info(`[${label}] Detaching channel in state:`, state);

    // If attaching, detach after attach
    if (state === 'attaching') {
      channel.once?.('attached', () => {
        channel.detach?.((err) => {
          if (err) console.warn(`[${label}] Detach after attach error:`, err);
        });
      });
      channel.once?.('failed', () => {});
      return;
    }

    if (state === 'attached' || state === 'suspended' || state === 'failed') {
      channel.detach?.((err) => {
        if (!err) return;
        const msg = String(err.message || '').toLowerCase();
        if (msg.includes('superseded')) {
          console.info(`[${label}] Ignored benign Ably detach error:`, err.message);
          return;
        }
        console.warn(`[${label}] Channel detach error:`, err);
      });
      return;
    }
  } catch (err) {
    console.warn(`[${label}] safeDetachAblyChannel caught error:`, err);
  }
}

/**
 * Subscribe to events on an Ably channel (async client, sync API)
 */
export function subscribeToEvent(channelName, eventName, handler) {
  let cancelled = false;
  let channel = null;
  let wrapped = null;

  (async () => {
    try {
      const client = await getSvcClient();
      if (cancelled || !client) return;

      channel = getSafeChannel(client, channelName) || client.channels.get(channelName);
      wrapped = (msg) => {
        try {
          handler(msg?.data ?? msg);
        } catch (e) {
          console.error('[realtime] handler error', e);
        }
      };

      channel.subscribe?.(eventName, wrapped);

      // Ensure attach (non-fatal if it fails)
      if (channel.state !== 'attached' && channel.state !== 'attaching') {
        channel.attach?.(() => {});
      }
    } catch (e) {
      console.warn('[realtime] subscribeToEvent failed (non-fatal):', e?.message || e);
    }
  })();

  return () => {
    cancelled = true;
    try {
      if (channel && wrapped) {
        channel.unsubscribe?.(eventName, wrapped);
        safeDetachAblyChannel(channel, channelName);
      }
    } catch {
      // swallow
    }
  };
}

/**
 * Subscribe to match messages (canonical channel: chat:match:${matchId})
 * Keeps same external API as before.
 */
export function subscribeToMatchMessages(matchId, handler) {
  if (!matchId) return () => {};

  const channelName = `chat:match:${matchId}`;
  return subscribeToEvent(channelName, 'message-created', handler);
}

/**
 * Backwards compatible getter (returns the service client promise)
 */
export function getRealtimeClient() {
  return getSvcClient();
}

export default { subscribeToEvent, getRealtimeClient, subscribeToMatchMessages };
